import React, { useState, useEffect } from 'react';
import Modal from './Modal';
import axiosInstance from "../utility/axiosInstance";
import { FaSyncAlt } from 'react-icons/fa';
import { useToast } from "./ToastContext";
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../store/store';
import { useNavigate } from 'react-router-dom';
import { fetchInstances } from '../store/slices/instancesSlice';

interface ClusterDetailsModalProps {
    isOpen: boolean;
    onClose: () => void;
    cluster: any;
}

const ClusterDetailsModal: React.FC<ClusterDetailsModalProps> = ({ isOpen, onClose, cluster }) => {
    const { showToast } = useToast();
    const navigate = useNavigate();
    const dispatch = useDispatch<AppDispatch>();
    const [details, setDetails] = useState<any>(null);
    const [loading, setLoading] = useState(true);

    const instances = useSelector((s: RootState) => s.instances.data);
    const stackName: string = cluster?.stack_name || '';

    // TWO STACK SHAPES, AND MATCHING ONLY ONE OF THEM IS A BUG THAT SHIPPED.
    //   app cluster       an autoscaling group, so a member's own nested stack is `<stack>-asg-<hash>`
    //   managed datastore a Heat ResourceGroup, whose members have NO `-asg` segment at all
    // Filtering on `-asg` alone showed "Members (0)" for every Postgres/ClickHouse HA cluster while
    // three members were up and serving. `cluster_name` cannot stand in for either: it is the
    // user-facing label and is identical across members.
    const members = instances.filter((i: any) => i.cluster_member && i.cluster_stack
        && (i.cluster_stack === stackName || i.cluster_stack.startsWith(stackName + '-asg')));

    // The hero is a ROLE held by exactly one live member, not a machine standing beside the cluster.
    // Older app clusters (built before the role model) still have a standalone source VM, so
    // `heroIsMember` decides which of the two the copy below describes. Getting this backwards told
    // a customer their serving Postgres primary "is not a member and does not serve cluster traffic".
    const heroVm = instances.find((i: any) => i.hero_of === stackName);
    const heroIsMember = !!heroVm && members.some((m: any) => m.id === heroVm.id);
    const dsRole: string | null = (heroVm?.cluster_role) || members.find((m: any) => m.cluster_role)?.cluster_role || null;
    const isDatastore = !!dsRole;
    // What the load balancer publishes to clients, per engine. Postgres answers on the POOLER
    // (6432), never 5432 - a connection string that keeps the old port simply fails to connect.
    const dsPort = dsRole === 'postgres-ha' ? 6432 : dsRole === 'clickhouse-ha' ? 8123 : null;
    // ⛔ Only HTTP-protocol datastores can ever be published: redu terminates TLS at the proxy and
    //    does not carry public TCP for anything but SSH. ClickHouse speaks HTTP on 8123 and could
    //    be; Postgres speaks its own TCP protocol and cannot.
    const dsCanBePublic = dsRole === 'clickhouse-ha';
    const privateIpOf = (i: any) => (Object.values(i.addresses || {}).flat() as any[]).find((a: any) => a["OS-EXT-IPS:type"] === "fixed")?.addr || '-';
    // Legacy "hero as baseline member" clusters: min_size 0, source VM in the pool, ASG adds extras.
    const isHeroBaseline = !heroIsMember && Number((details as any)?.parameters?.min_size) === 0;

    const fetchDetails = async () => {
        setLoading(true);
        try {
            const response = await axiosInstance.get(`/v1/clusters/${cluster.stack_name}/${cluster.id}`);
            setDetails(response.data);
        } catch (error) {
            console.error("Error fetching cluster details:", error);
            showToast("Failed to fetch cluster details", "error");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (isOpen && cluster) {
            fetchDetails();
            dispatch(fetchInstances());
        }
    }, [isOpen, cluster]);

    return (
        <Modal isOpen={isOpen} onClose={onClose} title={`Cluster Details: ${cluster?.stack_name}`} size="lg">
            <div style={{ padding: '20px' }}>
                {loading ? (
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px', flexDirection: 'column', gap: '10px' }}>
                        <FaSyncAlt className="spin" size={30} style={{ color: "var(--accent)" }} />
                        <span>Loading details...</span>
                    </div>
                ) : details ? (
                    <div className="cluster-details">
                        <div className="section" style={{ marginBottom: '25px' }}>
                            <h3 style={{ borderBottom: '1px solid var(--border)', paddingBottom: '10px', marginBottom: '15px', color: 'var(--text-primary)' }}>Status & Info</h3>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                                <div>
                                    <label style={{ fontWeight: 'bold', display: 'block', color: 'var(--text-muted)', fontSize: '0.85rem' }}>Status</label>
                                    <span className={`status-badge ${details.stack_status.toLowerCase().replace(/_/g, '-')}`}>
                                        {details.stack_status}
                                    </span>
                                </div>
                                <div>
                                    <label style={{ fontWeight: 'bold', display: 'block', color: 'var(--text-muted)', fontSize: '0.85rem' }}>ID</label>
                                    <code style={{ fontSize: '0.85rem' }}>{details.id}</code>
                                </div>
                                <div>
                                    <label style={{ fontWeight: 'bold', display: 'block', color: 'var(--text-muted)', fontSize: '0.85rem' }}>Created At</label>
                                    <span>{new Date(details.creation_time).toLocaleString()}</span>
                                </div>
                                <div>
                                    <label style={{ fontWeight: 'bold', display: 'block', color: 'var(--text-muted)', fontSize: '0.85rem' }}>Updated At</label>
                                    <span>{details.updated_time ? new Date(details.updated_time).toLocaleString() : 'N/A'}</span>
                                </div>
                                <div>
                                    <label style={{ fontWeight: 'bold', display: 'block', color: 'var(--text-muted)', fontSize: '0.85rem' }}>
                                        {isDatastore ? 'Connect at' : 'Load Balancer IP'}
                                    </label>
                                    {details.access_ip ? (
                                        <code style={{ fontSize: '0.85rem' }}>
                                            {details.access_ip}{isDatastore && dsPort ? `:${dsPort}` : ''}
                                        </code>
                                    ) : (
                                        <span style={{ color: 'var(--text-muted)' }}>Provisioning...</span>
                                    )}
                                </div>
                                <div style={{ gridColumn: '1 / -1' }}>
                                    <label style={{ fontWeight: 'bold', display: 'block', color: 'var(--text-muted)', fontSize: '0.85rem' }}>Domains</label>
                                    {(details.domains && details.domains.length > 0) ? (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                            {details.domains.map((d: any) => (
                                                <div key={d.proxyId} style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                                                    <a href={d.url} target="_blank" rel="noopener noreferrer" style={{ fontSize: '0.9rem', color: 'var(--accent)', wordBreak: 'break-all' }}>{d.url}</a>
                                                    <button
                                                        type="button"
                                                        onClick={() => { navigator.clipboard.writeText(d.url); showToast('URL copied', 'success'); }}
                                                        style={{ padding: '2px 8px', fontSize: '0.75rem', borderRadius: '5px', border: '1px solid var(--border)', background: 'var(--surface-alt)', color: 'var(--text-primary)', cursor: 'pointer' }}
                                                    >Copy</button>
                                                </div>
                                            ))}
                                        </div>
                                    ) : details.access_point ? (
                                        <a href={details.access_point} target="_blank" rel="noopener noreferrer" style={{ fontSize: '0.9rem', color: 'var(--accent)', wordBreak: 'break-all' }}>{details.access_point}</a>
                                    ) : isDatastore ? (
                                        <span style={{ color: 'var(--text-muted)' }}>
                                            Private. This cluster is reachable only from your private network.
                                        </span>
                                    ) : (
                                        <span style={{ color: 'var(--text-muted)' }}>Provisioning… (created once the load balancer has a public address)</span>
                                    )}
                                    <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', margin: '8px 0 0' }}>
                                        {isDatastore
                                            ? (dsCanBePublic
                                                ? 'Point your app at the address above, from an instance on the same private network. It speaks HTTP, so it can also be published on a public https:// name if you want one.'
                                                : 'Point your app at the address above, from an instance on the same private network. It speaks its own TCP protocol rather than HTTP, so it cannot be published on a public name. Reach it over the private network, or SSH to the hero below.')
                                            : 'Add your own domain from the Load Balancers tab (open the load balancer for this cluster, then the Domains section).'}
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/*<div className="section" style={{ marginBottom: '25px' }}>*/}
                        {/*    <h3 style={{ borderBottom: '1px solid var(--border)', paddingBottom: '10px', marginBottom: '15px', color: 'var(--text-primary)' }}>Parameters</h3>*/}
                        {/*    <div style={{ background: 'var(--surface-alt)', padding: '15px', borderRadius: '8px', border: '1px solid var(--border)', maxHeight: '400px', overflowY: 'auto', color: 'var(--text-primary)' }}>*/}
                        {/*        <table style={{ width: '100%', fontSize: '0.9rem' }}>*/}
                        {/*            <tbody>*/}
                        {/*                {Object.entries(details.parameters).map(([key, value]: [string, any]) => (*/}
                        {/*                    <tr key={key}>*/}
                        {/*                        <td style={{ fontWeight: 600, padding: '6px 8px', verticalAlign: 'top', width: '40%', color: 'var(--text-secondary)' }}>{key}:</td>*/}
                        {/*                        <td style={{ padding: '6px 8px', wordBreak: 'break-all', color: 'var(--text-primary)' }}>{String(value)}</td>*/}
                        {/*                    </tr>*/}
                        {/*                ))}*/}
                        {/*            </tbody>*/}
                        {/*        </table>*/}
                        {/*    </div>*/}
                        {/*</div>*/}
                        <div className="section" style={{ marginBottom: '25px' }}>
                            <h3 style={{ borderBottom: '1px solid var(--border)', paddingBottom: '10px', marginBottom: '15px', color: 'var(--text-primary)' }}>
                                {isHeroBaseline ? `Extra members (${members.length})` : `Members (${members.length})`}
                            </h3>
                            {members.length > 0 ? (
                                <table className="keypairs-table">
                                    <thead><tr><th>Name</th><th>Role</th><th>Status</th><th>Private IP</th></tr></thead>
                                    <tbody>
                                        {members.map((m: any) => (
                                            <tr key={m.id} onClick={() => navigate(`/category/compute/instances/${m.id}`)} style={{ cursor: 'pointer' }}>
                                                <td style={{ wordBreak: 'break-all' }}>{m.name}</td>
                                                <td>{m.hero_of === stackName
                                                    ? <span style={{ color: 'var(--accent)', fontWeight: 600 }}>★ hero</span>
                                                    : <span style={{ color: 'var(--text-muted)' }}>member</span>}</td>
                                                <td>{m.status}</td>
                                                <td>{privateIpOf(m)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            ) : (
                                <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', margin: 0 }}>
                                    {isHeroBaseline
                                        ? 'No extra members right now - only your source VM is serving. The group adds members automatically under load, then scales them back down to just your VM.'
                                        : isDatastore
                                            ? 'No members are reporting yet. They appear here as each one boots and joins the cluster.'
                                            : 'No members are running right now (the autoscaling group scales between its min and max size).'}
                                </p>
                            )}
                            {heroVm && (
                                <div style={{ marginTop: '14px', padding: '10px 12px', borderRadius: '6px', background: 'var(--surface-alt)', border: '1px solid var(--border)', fontSize: '0.82rem', color: 'var(--text-secondary)' }}>
                                    <strong style={{ color: 'var(--text-primary)' }}>
                                        {heroIsMember ? '★ Hero:' : isHeroBaseline ? 'Baseline member (source VM):' : 'Source VM (hero):'}
                                    </strong>{' '}
                                    <span onClick={() => navigate(`/category/compute/instances/${heroVm.id}`)} style={{ color: 'var(--accent)', cursor: 'pointer' }}>{heroVm.name}</span>.{' '}
                                    {heroVm.ssh_host && heroVm.ssh_port ? (
                                        <div style={{ marginTop: '8px', marginBottom: '8px' }}>
                                            <code style={{ fontSize: '0.8rem', wordBreak: 'break-all' }}>
                                                ssh -p {heroVm.ssh_port} ubuntu@{heroVm.ssh_host}
                                            </code>{' '}
                                            <button
                                                type="button"
                                                onClick={() => { navigator.clipboard.writeText(`ssh -p ${heroVm.ssh_port} ubuntu@${heroVm.ssh_host}`); showToast('SSH command copied', 'success'); }}
                                                style={{ padding: '2px 8px', fontSize: '0.72rem', borderRadius: '5px', border: '1px solid var(--border)', background: 'var(--surface-alt)', color: 'var(--text-primary)', cursor: 'pointer' }}
                                            >Copy</button>
                                            <span style={{ display: 'block', fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '4px' }}>
                                                This name is derived from the cluster, so it stays the same when the role moves to another member.
                                            </span>
                                        </div>
                                    ) : null}
                                    {heroIsMember
                                        ? (isDatastore
                                            ? 'Hero is a role, not a separate machine: this is one of the members above, and it is the one reachable over SSH. Kill it and another member is promoted, taking the role and the address with it.'
                                            : 'Hero is a role, not a separate machine: this is one of the members above and it serves cluster traffic like the rest. SSH into it, change it, then run a Rolling Update to roll your changes out to the other members. Kill it and another member is promoted, taking the role and the address with it.')
                                        : isHeroBaseline
                                            ? 'The VM this cluster was built from. It is the always-on baseline member and serves cluster traffic. SSH into it, change it, then run a Rolling Update to roll your changes out to the other members.'
                                            : 'The VM this cluster was built from. It is not a member and does not serve cluster traffic. SSH into it, change it, then run a Rolling Update to roll your changes out to the members.'}
                                </div>
                            )}
                        </div>

                        <div className="section" style={{ marginBottom: '25px' }}>
                            <h3 style={{ borderBottom: '1px solid var(--border)', paddingBottom: '10px', marginBottom: '15px', color: 'var(--text-primary)' }}>Parameters</h3>
                            <div style={{ background: 'var(--surface-alt)', padding: '15px', borderRadius: '8px', border: '1px solid var(--border)', maxHeight: '400px', overflowY: 'auto', color: 'var(--text-primary)' }}>
                                <table style={{ width: '100%', fontSize: '0.9rem' }}>
                                    <tbody>
                                    {Object.entries(details.parameters || {})
                                        .filter(([key]) => {
                                            // Define the keys you want to hide
                                            const hiddenKeys = [
                                                'backend_network_id',
                                                'lb_vip_subnet_id',
                                                'backend_subnet_id'
                                            ];

                                            // Filter out keys that start with OS:: OR are in our hidden list
                                            return !key.startsWith('OS::') && !hiddenKeys.includes(key);
                                        })
                                        .map(([key, value]: [string, any]) => (
                                            <tr key={key}>
                                                <td style={{ fontWeight: 600, padding: '6px 8px', verticalAlign: 'top', width: '40%', color: 'var(--text-secondary)' }}>
                                                    {key.replace(/_/g, ' ')}: {/* Optional: making keys look nicer */}
                                                </td>
                                                <td style={{ padding: '6px 8px', wordBreak: 'break-all', color: 'var(--text-primary)' }}>
                                                    {String(value)}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div style={{ textAlign: 'center', padding: '20px' }}>No details available.</div>
                )}

                <div className="modal-actions" style={{ marginTop: '30px' }}>
                    <button className="cancel-button" onClick={onClose}>Close</button>
                    <button className="submit-button" onClick={fetchDetails} style={{ width: 'auto' }}>
                        <FaSyncAlt style={{ marginRight: '8px' }} />
                        Refresh Details
                    </button>
                </div>
            </div>
        </Modal>
    );
};

export default ClusterDetailsModal;
